/**
 * ⟦C62 · lot 4 · friction 4⟧ **Le retour de « Laisser tourner » en mots**, sans rien d'autre : pas de store, pas de
 * contenu, pas d'écran. Ce fichier ne fait que mettre en une ligne un `RecapDelegue` que le moteur a MESURÉ
 * (jours, vidéos, abonnés avant et après, motif d'arrêt) ; il se teste sans navigateur.
 *
 * Deux sorties, consommées par `retourEquipe.ts` :
 *
 *  - la **phrase** (« L'équipe a tenu 3 jours : 2 vidéos, +1 511 abonnés. Un événement t'attend. »), au toast
 *    et en tête du récapitulatif de l'écran de l'Équipe ;
 *  - la **légende** (« Dernier tour : 3 jours, 2 vidéos, +1 511 abonnés »), sous le bouton de la fenêtre Actions.
 *
 * Les motifs d'arrêt viennent du contenu (`producteur.json`, `libelles_courts.arret`) : sans ce bloc, la phrase
 * s'arrête aux chiffres (l'état neutre : un contenu d'avant le chantier se lit comme avant).
 */

import { H_JOUR, type GameState, type MotifArret, type RecapDelegue } from "@maxime/shared";

import { nombre } from "../../game/format";

/** Quand une clé du contenu manque : le mot d'avant le chantier. */
const ARRET_PAR_DEFAUT = "On a besoin de toi.";

/** Ces arrêts-là sont des alertes : le toast prend le liseré rouge. */
const ARRETS_GRAVES: ReadonlySet<string> = new Set<MotifArret>(["peril", "sante_mentale", "tresorerie"]);

/** Après une semaine de jeu, « Dernier tour » ne dit plus rien d'utile : la légende s'efface. */
export const JOURS_DE_MEMOIRE = 7;

export interface ResumeDelegation {
  /** « 3 jours » ou « moins d'un jour ». */
  duree: string;
  /** « 2 vidéos », « 1 vidéo » ou « aucune vidéo ». */
  videos: string;
  /** « +1 511 abonnés », « −26 abonnés », ou `null` quand le compteur n'a pas bougé. */
  abonnes: string | null;
  /** L'équipe n'a joué aucune action : la main est revenue tout de suite. */
  rien: boolean;
  /** La demi-phrase du motif d'arrêt, ou `null` si le contenu n'en porte pas. */
  arret: string | null;
  /** La ligne entière : le toast. */
  phrase: string;
  /** L'historique seul : la légende du bouton. */
  legende: string;
  ton: "info" | "mal";
}

/**
 * Met un récapitulatif de délégation en une ligne. Pure : aucun accès au store ni au contenu, la table des
 * motifs courts est passée par l'appelant (testable sans navigateur).
 */
export function resumerDelegation(
  recap: RecapDelegue,
  arretsCourts: Readonly<Record<string, string>> | undefined,
): ResumeDelegation {
  const jours = Math.max(0, Math.round(recap.jours));
  const duree = jours >= 1 ? `${nombre(jours)} jour${jours > 1 ? "s" : ""}` : "moins d'un jour";
  const v = Math.max(0, Math.round(recap.videos));
  const videos = v === 0 ? "aucune vidéo" : `${nombre(v)} vidéo${v > 1 ? "s" : ""}`;
  const net = Math.round(recap.abonnes.apres - recap.abonnes.avant);
  const abonnes =
    net === 0 ? null : `${net > 0 ? "+" : "−"}${nombre(Math.abs(net))} abonné${Math.abs(net) > 1 ? "s" : ""}`;
  const rien = recap.actions === 0;
  const arret = arretsCourts ? (arretsCourts[recap.arret] ?? ARRET_PAR_DEFAUT) : null;

  const chiffres = [videos, abonnes].filter((x): x is string => x !== null).join(", ");
  const phrase = rien
    ? `L'équipe n'a rien lancé.${arret ? ` ${arret}` : ""}`
    : `L'équipe a tenu ${duree} : ${chiffres}.${arret ? ` ${arret}` : ""}`;
  const legende = rien ? "Dernier tour : rien lancé" : `Dernier tour : ${duree}, ${chiffres}`;

  return { duree, videos, abonnes, rien, arret, phrase, legende, ton: ARRETS_GRAVES.has(recap.arret) ? "mal" : "info" };
}

export interface MemoireRetour {
  /** Le dernier récapitulatif rendu par `laisser_tourner`, ou `null`. */
  recap: RecapDelegue | null;
  /** La run à laquelle il appartient : une nouvelle partie n'hérite pas du retour de la précédente. */
  runId: string | null;
}

/** Le retour à montrer pour CETTE partie à CET instant, ou `null`. */
export function retourVisible(
  memoire: MemoireRetour,
  runId: string | undefined,
  state: GameState,
): RecapDelegue | null {
  if (memoire.recap === null || runId === undefined || memoire.runId !== runId) return null;
  if (state.clock_h < memoire.recap.au_h) return null;
  if (state.clock_h - memoire.recap.au_h > JOURS_DE_MEMOIRE * H_JOUR) return null;
  return memoire.recap;
}
